import { InformationCircleIcon } from '@heroicons/react/24/solid';

import { Input, Type } from '@/components/form/Input';
import { Tooltip } from '@/components/Tooltip';
import { CapabilityParameter } from '@/hooks/useCapabilities';

interface ParameterInputProps {
  param: CapabilityParameter;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  readOnly?: boolean;
  isRequired?: boolean;
  onBlur?: (paramName: string) => void;
}

/**
 * Helper function to detect if a parameter should be treated as a password field
 */
function isPasswordField(param: CapabilityParameter): boolean {
  const checkText = (text: string | undefined) =>
    text?.toLowerCase().includes('password') ?? false;

  return checkText(param.name) || checkText(param.description);
}

/**
 * ParameterInput - Unified component for rendering capability parameter inputs
 *
 * Handles both required and optional parameters, with read-only mode for customers
 * and editable mode for Praetorian users. Automatically detects password fields
 * and applies appropriate input masking.
 */
export function ParameterInput({
  param,
  value,
  onChange,
  error,
  readOnly = false,
  isRequired = false,
  onBlur,
}: ParameterInputProps) {
  // For read-only mode (customers), render as key-value pairs
  if (readOnly) {
    return (
      <div className="flex items-center gap-4">
        <div className={`${isRequired ? 'w-48' : 'w-40'} shrink-0`}>
          <span className="text-chariot-text-primary text-sm font-bold">
            {param.name}
          </span>
        </div>
        <div className="flex-1">
          <span className="text-chariot-text-secondary text-sm">
            {param.default || value || 'Not configured'}
          </span>
        </div>
      </div>
    );
  }

  // Editable input for Praetorian users
  return (
    <div className="flex items-center gap-4">
      <div className="flex w-40 shrink-0 items-center gap-2">
        <label className="text-chariot-text-primary text-sm font-medium">
          {param.name}
          {isRequired && <span className="ml-0.5 text-red-500">*</span>}
        </label>
        {param.description && (
          <Tooltip
            title={param.description}
            placement="top"
            tooltipClassName="max-w-sm whitespace-normal break-words"
          >
            <InformationCircleIcon className="text-chariot-stroke size-4" />
          </Tooltip>
        )}
      </div>
      <div className="flex-1">
        <Input
          name={param.name}
          type={isPasswordField(param) ? Type.PASSWORD : undefined}
          password={isPasswordField(param)}
          placeholder={param.default || `Enter ${param.name.toLowerCase()}...`}
          value={value}
          onChange={e => onChange(e.target.value)}
          onBlur={() => onBlur?.(param.name)}
          error={error}
          className="w-full"
          aria-label={`${param.name} input${isRequired ? ' (required)' : ''}`}
        />
      </div>
    </div>
  );
}
